import { useFrame } from '@react-three/fiber'
import { useRef } from 'react'
import * as THREE from 'three'
import { CaixaArredondada } from '../../componentes/CaixaArredondada'

/**
 * Peças comuns às viaturas do Table top: roda, giroflex, faixas e texturas.
 *
 * Existem para que caminhão, resgate e o que vier depois tenham o MESMO nível
 * de acabamento — roda com porca, persiana com lâmina, faixa com chevron —, e
 * não cada um o seu.
 */

const semRaycast = () => null

let texturaPersiana: THREE.CanvasTexture | null = null
/** Lâminas horizontais de porta de enrolar em alumínio: gerada uma vez e compartilhada. */
export function texturaDePersiana(): THREE.CanvasTexture | null {
  if (texturaPersiana) return texturaPersiana
  if (typeof document === 'undefined') return null
  const canvas = document.createElement('canvas')
  canvas.width = 16
  canvas.height = 256
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  for (let y = 0; y < canvas.height; y += 16) {
    const g = ctx.createLinearGradient(0, y, 0, y + 16)
    g.addColorStop(0, '#f1f5f9')
    g.addColorStop(0.55, '#cbd5e1')
    g.addColorStop(0.9, '#8a97a8')
    g.addColorStop(1, '#5d6878')
    ctx.fillStyle = g
    ctx.fillRect(0, y, canvas.width, 16)
  }
  texturaPersiana = new THREE.CanvasTexture(canvas)
  texturaPersiana.colorSpace = THREE.SRGBColorSpace
  texturaPersiana.wrapT = THREE.RepeatWrapping
  return texturaPersiana
}

let texturaChevron: THREE.CanvasTexture | null = null
/** Faixas diagonais vermelho/amarelo da traseira de viatura (chevron). */
export function texturaDeChevron(): THREE.CanvasTexture | null {
  if (texturaChevron) return texturaChevron
  if (typeof document === 'undefined') return null
  const canvas = document.createElement('canvas')
  canvas.width = 256
  canvas.height = 128
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  ctx.fillStyle = '#facc15'
  ctx.fillRect(0, 0, 256, 128)
  ctx.fillStyle = '#dc2626'
  for (let x = -128; x < 384; x += 64) {
    ctx.beginPath()
    ctx.moveTo(x, 128)
    ctx.lineTo(x + 32, 128)
    ctx.lineTo(x + 32 + 64, 0)
    ctx.lineTo(x + 64, 0)
    ctx.closePath()
    ctx.fill()
  }
  texturaChevron = new THREE.CanvasTexture(canvas)
  texturaChevron.colorSpace = THREE.SRGBColorSpace
  return texturaChevron
}

const texturasLetreiro = new Map<string, THREE.CanvasTexture>()
/** Letreiro em branco sobre fundo transparente, para portas e laterais. */
export function texturaDeLetreiro(texto: string, linha2?: string): THREE.CanvasTexture | null {
  const chave = `${texto}|${linha2 ?? ''}`
  const pronta = texturasLetreiro.get(chave)
  if (pronta) return pronta
  if (typeof document === 'undefined') return null
  const canvas = document.createElement('canvas')
  canvas.width = 512
  canvas.height = 256
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  ctx.fillStyle = '#ffffff'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.font = 'bold 92px sans-serif'
  ctx.fillText(texto, 256, linha2 ? 92 : 128)
  if (linha2) {
    ctx.font = 'bold 110px sans-serif'
    ctx.fillText(linha2, 256, 196)
  }
  const textura = new THREE.CanvasTexture(canvas)
  textura.colorSpace = THREE.SRGBColorSpace
  texturasLetreiro.set(chave, textura)
  return textura
}

/**
 * Roda de viatura: pneu com flanco arredondado, aro de aço, cubo e porcas.
 * O eixo da roda é Z (lateral do veículo, que corre ao longo de X).
 * `dupla` põe o segundo pneu por dentro, como no eixo traseiro de caminhão.
 */
export function RodaViatura({
  posicao,
  raio = 0.5,
  largura = 0.32,
  lado,
  dupla = false,
  esterco = 0,
}: {
  posicao: [number, number, number]
  raio?: number
  largura?: number
  /** +1 lado +z, -1 lado -z: a face do aro olha para fora */
  lado: 1 | -1
  dupla?: boolean
  esterco?: number
}) {
  const pneus = dupla ? [0, -lado * (largura + 0.02)] : [0]
  return (
    <group position={posicao} rotation={[0, esterco, 0]}>
      {pneus.map((dz, i) => (
        <group key={i} position={[0, 0, dz]} rotation={[Math.PI / 2, 0, 0]}>
          <mesh raycast={semRaycast} castShadow>
            <cylinderGeometry args={[raio, raio, largura * 0.82, 28]} />
            <meshStandardMaterial color="#141619" roughness={0.92} />
          </mesh>
          {/* flancos arredondados */}
          {[-1, 1].map((f) => (
            <mesh key={f} raycast={semRaycast} position={[0, (f * largura * 0.41), 0]} rotation={[Math.PI / 2, 0, 0]}>
              <torusGeometry args={[raio - largura * 0.09, largura * 0.11, 8, 28]} />
              <meshStandardMaterial color="#1b1e22" roughness={0.9} />
            </mesh>
          ))}
        </group>
      ))}
      {/* aro, cubo e porcas: só na face de fora */}
      <group position={[0, 0, lado * largura * 0.43]} rotation={[Math.PI / 2, 0, 0]}>
        <mesh raycast={semRaycast}>
          <cylinderGeometry args={[raio * 0.62, raio * 0.62, 0.03, 24]} />
          <meshStandardMaterial color="#c7ccd2" metalness={0.75} roughness={0.32} />
        </mesh>
        <mesh raycast={semRaycast} position={[0, lado * 0.03, 0]}>
          <cylinderGeometry args={[raio * 0.22, raio * 0.26, 0.06, 16]} />
          <meshStandardMaterial color="#8d949c" metalness={0.8} roughness={0.3} />
        </mesh>
        {Array.from({ length: 8 }, (_, i) => {
          const a = (i / 8) * Math.PI * 2
          return (
            <mesh key={i} raycast={semRaycast} position={[Math.cos(a) * raio * 0.36, lado * 0.035, Math.sin(a) * raio * 0.36]}>
              <cylinderGeometry args={[0.022, 0.022, 0.04, 6]} />
              <meshStandardMaterial color="#e5e7eb" metalness={0.85} roughness={0.25} />
            </mesh>
          )
        })}
      </group>
    </group>
  )
}

/**
 * Barra de giroflex: base escura, módulos vermelho e azul alternando, centro
 * branco. Duas luzes pontuais acendem junto com as lentes.
 */
export function Giroflex({
  posicao,
  comprimento,
}: {
  posicao: [number, number, number]
  /** ao longo de Z (largura do veículo) */
  comprimento: number
}) {
  const vermelho = useRef<THREE.MeshStandardMaterial>(null)
  const azul = useRef<THREE.MeshStandardMaterial>(null)
  const luzVermelha = useRef<THREE.PointLight>(null)
  const luzAzul = useRef<THREE.PointLight>(null)
  useFrame(({ clock }) => {
    const fase = Math.floor(clock.elapsedTime * 7) % 4
    const v = fase < 2
    if (vermelho.current) vermelho.current.emissiveIntensity = v ? 3.5 : 0.2
    if (azul.current) azul.current.emissiveIntensity = v ? 0.2 : 3.5
    if (luzVermelha.current) luzVermelha.current.intensity = v ? 2.5 : 0
    if (luzAzul.current) luzAzul.current.intensity = v ? 0 : 2.5
  })
  const modulo = comprimento * 0.36
  return (
    <group position={posicao}>
      <mesh raycast={semRaycast} castShadow>
        <CaixaArredondada args={[0.34, 0.07, comprimento]} raio={0.02} />
        <meshStandardMaterial color="#111418" roughness={0.6} />
      </mesh>
      <mesh raycast={semRaycast} position={[0, 0.08, -comprimento / 2 + modulo / 2 + 0.03]}>
        <CaixaArredondada args={[0.28, 0.11, modulo]} raio={0.03} />
        <meshStandardMaterial ref={vermelho} color="#ef4444" emissive="#ef4444" emissiveIntensity={2.5} roughness={0.15} />
      </mesh>
      <mesh raycast={semRaycast} position={[0, 0.08, 0]}>
        <CaixaArredondada args={[0.24, 0.1, comprimento - modulo * 2 - 0.1]} raio={0.03} />
        <meshStandardMaterial color="#f8fafc" emissive="#ffffff" emissiveIntensity={1} roughness={0.2} />
      </mesh>
      <mesh raycast={semRaycast} position={[0, 0.08, comprimento / 2 - modulo / 2 - 0.03]}>
        <CaixaArredondada args={[0.28, 0.11, modulo]} raio={0.03} />
        <meshStandardMaterial ref={azul} color="#3b82f6" emissive="#3b82f6" emissiveIntensity={2.5} roughness={0.15} />
      </mesh>
      <pointLight ref={luzVermelha} color="#ef4444" distance={9} intensity={2} position={[0, 0.4, -comprimento / 2]} />
      <pointLight ref={luzAzul} color="#3b82f6" distance={9} intensity={2} position={[0, 0.4, comprimento / 2]} />
    </group>
  )
}

/** Retrovisor de viatura: braço e espelho, do lado `lado` (+1/-1 em z). */
export function RetrovisorViatura({ posicao, lado }: { posicao: [number, number, number]; lado: 1 | -1 }) {
  return (
    <group position={posicao}>
      <mesh raycast={semRaycast} position={[0, 0, lado * 0.1]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.015, 0.015, 0.22, 6]} />
        <meshStandardMaterial color="#1a1d21" metalness={0.5} roughness={0.5} />
      </mesh>
      <mesh raycast={semRaycast} castShadow position={[0, -0.05, lado * 0.22]}>
        <CaixaArredondada args={[0.07, 0.42, 0.18]} raio={0.03} />
        <meshStandardMaterial color="#1a1d21" roughness={0.5} />
      </mesh>
      <mesh raycast={semRaycast} position={[0.04, -0.05, lado * 0.22]}>
        <boxGeometry args={[0.005, 0.36, 0.14]} />
        <meshStandardMaterial color="#9fb3c8" metalness={0.9} roughness={0.08} />
      </mesh>
    </group>
  )
}
